import { useState } from 'react';
import { Box, styled } from '@mui/material';
import { TFormType } from 'src/@types';
import { DeviceToggle } from 'src/components/device-toggle/DeviceToggle';
import { TDeviceType } from 'src/static/constants';
import { toFileItem } from 'src/utils/fileUploadUtils';

import { GuideArea } from '../guide/ServiceGuide';
import MediaRenderer from '../hook-form/uploadImage/sections/MediaRenderer';

interface PreviewProps {
  formType?: TFormType;
  areas: GuideArea[];
  values?: Record<string, any>;
}

const Preview = ({ values }: PreviewProps) => {
  const [device, setDevice] = useState<TDeviceType>('desktop');

  return (
    <PreviewContainer>
      <DeviceToggle
        device={device}
        onDeviceChange={(_, newDevice) => newDevice && setDevice(newDevice)}
      />
      <PreviewFrame device={device}>
        {values?.bgPath?.[0] && (
          <MediaWrapper>
            <MediaRenderer file={toFileItem(values.bgPath[0], 0)} />
          </MediaWrapper>
        )}
        <CenterContent>
          <TypographyStyled
            device={device}
            style={{ color: values?.mainText?.color }}
            dangerouslySetInnerHTML={{
              __html: values?.mainText?.text?.replace(/\n/g, '<br/>') || 'Default Title',
            }}
          />
          <Subtitle
            device={device}
            style={{ color: values?.subText?.color }}
            dangerouslySetInnerHTML={{
              __html: values?.subText?.text?.replace(/\n/g, '<br/>') || 'Default Subtitle',
            }}
          />
        </CenterContent>
      </PreviewFrame>
    </PreviewContainer>
  );
};

export default Preview;

const PreviewContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
});

const PreviewFrame = styled(Box)<{ device: TDeviceType }>(({ device }) => ({
  position: 'relative',
  width: device === 'mobile' ? '375px' : device === 'tablet' ? '80%' : '100%',
  height: device === 'mobile' ? '667px' : '500px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
}));

const MediaWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  '& img, & video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const CenterContent = styled(Box)({
  position: 'relative',
  zIndex: 1,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  whiteSpace: 'pre-wrap',
});

const TypographyStyled = styled('h1')<{ device: string }>(({ device }) => ({
  fontSize: device === 'mobile' ? '20px' : device === 'tablet' ? '24px' : '32px',
  lineHeight: device === 'mobile' ? '28px' : '36px',
  marginBottom: '16px',
}));

const Subtitle = styled('p')<{ device: string }>(({ device }) => ({
  fontSize: device === 'mobile' ? '14px' : device === 'tablet' ? '18px' : '20px',
  lineHeight: device === 'mobile' ? '20px' : '28px',
  whiteSpace: 'pre-wrap',
}));
